/**
 * Greystone Scroll Reveal
 * IntersectionObserver-based scroll animations
 * Converted from: src/hooks/useScrollReveal.ts
 */
(function() {
  'use strict';

  function initScrollReveal() {
    var elements = document.querySelectorAll('[data-scroll-reveal]');

    if (!elements.length) return;

    // Check if IntersectionObserver is supported
    if (!('IntersectionObserver' in window)) {
      // Fallback: just show all elements
      elements.forEach(function(el) {
        el.classList.remove('opacity-0');
        el.classList.add('animate-fade-in-up');
      });
      return;
    }

    var observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in-up');
          entry.target.classList.remove('opacity-0');
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.15,
      rootMargin: '0px 0px -50px 0px'
    });

    elements.forEach(function(el) {
      el.classList.add('opacity-0');
      observer.observe(el);
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initScrollReveal);
  } else {
    initScrollReveal();
  }

  // Re-initialize when Shopify section is re-rendered
  document.addEventListener('shopify:section:load', initScrollReveal);
})();
/**
 * Greystone Smooth Scroll
 * Smooth scroll navigation utility
 */
(function() {
  'use strict';

  function initSmoothScroll() {
    document.addEventListener('click', function(e) {
      var target = e.target.closest('[data-scroll-to]');
      if (!target) return;

      e.preventDefault();

      var targetId = target.dataset.scrollTo;
      var targetEl = document.getElementById(targetId);

      if (!targetEl) {
        // Try querySelector for data-section attribute
        targetEl = document.querySelector('[data-section="' + targetId + '"]');
      }

      if (targetEl) {
        var offset = parseInt(target.dataset.scrollOffset) || 0;
        var headerHeight = 80; // Default header height
        var totalOffset = offset + headerHeight;

        var top = targetEl.getBoundingClientRect().top + window.scrollY - totalOffset;

        window.scrollTo({
          top: top,
          behavior: 'smooth'
        });
      }
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSmoothScroll);
  } else {
    initSmoothScroll();
  }
})();
/**
 * Greystone Carousel
 * Testimonials and image gallery carousel
 * Converted from: src/components/Testimonials.tsx
 */
(function() {
  'use strict';

  function TestimonialsCarousel(container) {
    var track = container.querySelector('[data-carousel-track]');
    var prevBtn = container.querySelector('[data-carousel-prev]');
    var nextBtn = container.querySelector('[data-carousel-next]');
    var dots = container.querySelectorAll('[data-carousel-dot]');
    var items = container.querySelectorAll('[data-carousel-item]');

    if (!track || items.length === 0) return;

    var current = 0;
    var perView = window.innerWidth >= 1024 ? 3 : 1;
    var maxIndex = Math.max(0, items.length - perView);

    function updateView() {
      var cardWidthPercent = 100 / perView;
      track.style.transform = 'translateX(-' + (current * cardWidthPercent) + '%)';
      track.style.transition = 'transform 0.5s ease';

      // Update buttons
      if (prevBtn) {
        prevBtn.disabled = current === 0;
        prevBtn.style.opacity = current === 0 ? '0.5' : '1';
      }
      if (nextBtn) {
        nextBtn.disabled = current >= maxIndex;
        nextBtn.style.opacity = current >= maxIndex ? '0.5' : '1';
      }

      // Update dots
      dots.forEach(function(dot, i) {
        if (i === current) {
          dot.classList.add('bg-accent');
          dot.classList.remove('bg-border');
        } else {
          dot.classList.remove('bg-accent');
          dot.classList.add('bg-border');
        }
      });
    }

    function handleResize() {
      var newPerView = window.innerWidth >= 1024 ? 3 : 1;
      if (newPerView !== perView) {
        perView = newPerView;
        maxIndex = Math.max(0, items.length - perView);
        current = Math.min(current, maxIndex);
        updateView();
      }
    }

    // Event listeners
    if (prevBtn) {
      prevBtn.addEventListener('click', function() {
        current = Math.max(0, current - 1);
        updateView();
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', function() {
        current = Math.min(maxIndex, current + 1);
        updateView();
      });
    }

    dots.forEach(function(dot, i) {
      dot.addEventListener('click', function() {
        if (i <= maxIndex) {
          current = i;
          updateView();
        }
      });
    });

    // Resize listener with debounce
    var resizeTimeout;
    window.addEventListener('resize', function() {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(handleResize, 150);
    });

    // Initial render
    updateView();
  }

  // Image Gallery Carousel (simpler version for product images)
  function ImageGallery(container) {
    var images = container.querySelectorAll('[data-gallery-image]');
    var thumbs = container.querySelectorAll('[data-gallery-thumb]');
    var prevBtn = container.querySelector('[data-gallery-prev]');
    var nextBtn = container.querySelector('[data-gallery-next]');
    var counter = container.querySelector('[data-gallery-counter]');

    if (images.length === 0) return;

    var current = 0;

    function updateGallery() {
      images.forEach(function(img, i) {
        img.style.display = i === current ? 'block' : 'none';
      });

      thumbs.forEach(function(thumb, i) {
        if (i === current) {
          thumb.classList.add('border-accent', 'shadow-sm', 'ring-2', 'ring-accent/20');
          thumb.classList.remove('border-border', 'opacity-70');
        } else {
          thumb.classList.remove('border-accent', 'shadow-sm', 'ring-2', 'ring-accent/20');
          thumb.classList.add('border-border', 'opacity-70');
        }
      });

      if (counter) {
        counter.textContent = (current + 1) + ' / ' + images.length;
      }
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', function() {
        current = current === 0 ? images.length - 1 : current - 1;
        updateGallery();
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', function() {
        current = current === images.length - 1 ? 0 : current + 1;
        updateGallery();
      });
    }

    thumbs.forEach(function(thumb, i) {
      thumb.addEventListener('click', function() {
        current = i;
        updateGallery();
      });
    });

    updateGallery();
  }

  function initCarousels() {
    // Initialize testimonials carousels
    document.querySelectorAll('[data-testimonials-carousel]').forEach(TestimonialsCarousel);

    // Initialize image galleries
    document.querySelectorAll('[data-image-gallery]').forEach(ImageGallery);
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCarousels);
  } else {
    initCarousels();
  }

  // Re-initialize when Shopify section is re-rendered
  document.addEventListener('shopify:section:load', initCarousels);
})();
/**
 * Greystone Product Selector
 * Model switcher and product gallery functionality
 * Converted from: src/components/ProductShowcase.tsx, ProductHero.tsx
 */
(function() {
  'use strict';

  function ProductSelector(container) {
    var modelBtns = container.querySelectorAll('[data-model-btn]');
    var modelPanels = container.querySelectorAll('[data-model-panel]');

    if (modelBtns.length === 0) return;

    var currentModel = 0;

    // Find initially active model
    modelBtns.forEach(function(btn, i) {
      if (btn.classList.contains('bg-primary') || btn.dataset.active === 'true') {
        currentModel = i;
      }
    });

    function selectModel(idx) {
      currentModel = idx;

      // Update button styles
      modelBtns.forEach(function(btn, i) {
        if (i === idx) {
          btn.classList.add('bg-primary', 'text-primary-foreground', 'border-primary');
          btn.classList.remove('bg-card', 'text-foreground', 'border-border');
        } else {
          btn.classList.remove('bg-primary', 'text-primary-foreground', 'border-primary');
          btn.classList.add('bg-card', 'text-foreground', 'border-border');
        }
      });

      // Show/hide panels
      modelPanels.forEach(function(panel, i) {
        panel.style.display = i === idx ? 'block' : 'none';
      });

      // Reset image gallery in new panel if exists
      var newPanel = modelPanels[idx];
      if (newPanel) {
        var gallery = newPanel.querySelector('[data-image-gallery]');
        if (gallery && window.GreystoneGallery) {
          window.GreystoneGallery.reset(gallery);
        }
      }
    }

    // Bind click events
    modelBtns.forEach(function(btn, i) {
      btn.addEventListener('click', function() {
        selectModel(i);
      });
    });

    // Initialize
    selectModel(currentModel);
  }

  // Product Card Picker (for model comparison cards)
  function ProductCardPicker(container) {
    var cards = container.querySelectorAll('[data-product-card]');
    var mainDisplay = container.querySelector('[data-main-display]');

    if (cards.length === 0) return;

    var currentCard = 0;

    // Find initially active card
    cards.forEach(function(card, i) {
      if (card.dataset.active === 'true') {
        currentCard = i;
      }
    });

    function selectCard(idx) {
      currentCard = idx;

      cards.forEach(function(card, i) {
        var isActive = i === idx;

        if (isActive) {
          card.classList.add('border-cta', 'shadow-2xl', 'ring-4', 'ring-cta/15', 'scale-[1.03]');
          card.classList.remove('border-border/60', 'opacity-70');
        } else {
          card.classList.remove('border-cta', 'shadow-2xl', 'ring-4', 'ring-cta/15', 'scale-[1.03]');
          card.classList.add('border-border/60', 'opacity-70');
        }

        // Update footer
        var footer = card.querySelector('[data-card-footer]');
        var activeFooter = card.querySelector('[data-card-footer-active]');
        var inactiveFooter = card.querySelector('[data-card-footer-inactive]');

        if (activeFooter) activeFooter.style.display = isActive ? 'flex' : 'none';
        if (inactiveFooter) inactiveFooter.style.display = isActive ? 'none' : 'flex';
      });

      // Update main display if exists
      if (mainDisplay) {
        var panels = mainDisplay.querySelectorAll('[data-display-panel]');
        panels.forEach(function(panel, i) {
          panel.style.display = i === idx ? 'block' : 'none';
        });
      }
    }

    cards.forEach(function(card, i) {
      card.addEventListener('click', function() {
        selectCard(i);

        // Scroll to top of component if main display exists
        if (mainDisplay) {
          var top = container.getBoundingClientRect().top + window.scrollY - 100;
          window.scrollTo({ top: top, behavior: 'smooth' });
        }
      });
    });

    selectCard(currentCard);
  }

  function initProductSelectors() {
    document.querySelectorAll('[data-product-selector]').forEach(ProductSelector);
    document.querySelectorAll('[data-product-card-picker]').forEach(ProductCardPicker);
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProductSelectors);
  } else {
    initProductSelectors();
  }

  // Re-initialize when Shopify section is re-rendered
  document.addEventListener('shopify:section:load', initProductSelectors);
})();
/**
 * Greystone Tab Navigation
 * Sticky tab navigation with active state tracking
 * Converted from: src/components/ProductTabNav.tsx
 */
(function() {
  'use strict';

  function TabNav(nav) {
    var tabs = nav.querySelectorAll('[data-tab-target]');

    if (tabs.length === 0) return;

    var sectionIds = [];
    tabs.forEach(function(tab) {
      sectionIds.push(tab.dataset.tabTarget);
    });

    function updateActiveTab(activeId) {
      tabs.forEach(function(tab) {
        var isActive = tab.dataset.tabTarget === activeId;

        if (isActive) {
          tab.classList.add('border-accent', 'text-accent');
          tab.classList.remove('border-transparent', 'text-muted-foreground');
        } else {
          tab.classList.remove('border-accent', 'text-accent');
          tab.classList.add('border-transparent', 'text-muted-foreground');
        }
      });
    }

    // IntersectionObserver for tracking active section
    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting) {
            updateActiveTab(entry.target.id);
          }
        });
      }, {
        rootMargin: '-25% 0px -65% 0px'
      });

      sectionIds.forEach(function(id) {
        var el = document.getElementById(id);
        if (el) {
          observer.observe(el);
        }
      });
    }

    // Click handlers for smooth scroll
    tabs.forEach(function(tab) {
      tab.addEventListener('click', function(e) {
        e.preventDefault();

        var targetId = tab.dataset.tabTarget;
        var el = document.getElementById(targetId);

        if (el) {
          var offset = parseInt(tab.dataset.scrollOffset) || 120;
          var top = el.getBoundingClientRect().top + window.scrollY - offset;

          window.scrollTo({
            top: top,
            behavior: 'smooth'
          });

          // Update active state immediately
          updateActiveTab(targetId);
        }
      });
    });

    // Set initial active state
    if (sectionIds.length > 0) {
      updateActiveTab(sectionIds[0]);
    }
  }

  function initTabNav() {
    document.querySelectorAll('[data-tab-nav]').forEach(TabNav);
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTabNav);
  } else {
    initTabNav();
  }

  // Re-initialize when Shopify section is re-rendered
  document.addEventListener('shopify:section:load', initTabNav);
})();
